import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Download, Loader2, RefreshCw, Share2, Shuffle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { getAdminSpeechTopics } from '@/api/speechTopic';
import { CONTENT_CATEGORY_LABELS, type ContentCategory } from '@shared/api.interface';
import { CATEGORY_STYLES } from '@/utils/topic-category-style';
import { extractApiErrorMessage } from '../api-error';
import {
  POSTER_COMBOS,
  POSTER_TEMPLATES,
  buildCombo,
  isTemplateAvailable,
  rebuildSlot,
  type PosterComboId,
  type PosterScope,
  type PosterSlot,
  type PosterTemplateId,
  type PosterTopic,
} from './poster-plan';
import { preloadPosterImages } from './poster-images';
import { createPosterZip } from './poster-zip';
import { POSTER_HEIGHT, POSTER_WIDTH, Poster, collectPosterImages } from './PosterTemplates';

const SCOPES: Array<{ value: PosterScope; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'hot', label: '热门' },
  { value: 'new', label: '最新' },
  ...(Object.keys(CATEGORY_STYLES) as ContentCategory[]).map((key) => ({ value: key, label: CONTENT_CATEGORY_LABELS[key] })),
];

const COMBO_HINTS: Record<PosterComboId, string> = {
  overview: '大卡片 → 封面墙 → 筛选展示 → 列表模式 → 详情弹窗',
  single: '5 个不同主题，每张一个详情弹窗（可单独切成资源双联）',
  mixed: '封面墙 → 详情弹窗 → 详情弹窗 → 资源双联 → 封面墙（换个分类）',
  quality: '大卡片 → 八大分类 → PPT 内页墙 → 资源双联 → 资源清单',
  album: '八大分类 → 中国精神专题页 → 中国智慧卡片墙 → 中国特色专题页 → 筛选展示',
};

interface ExportedImage {
  file: File;
  url: string;
}

/** 按容器宽度缩放 1080×1440 的成图 */
function ScaledPoster({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const update = () => setWidth(box.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  const scale = width / POSTER_WIDTH;
  return (
    <div
      ref={boxRef}
      className={`relative w-full overflow-hidden rounded-md bg-muted shadow-sm ${className}`}
      style={{ aspectRatio: `${POSTER_WIDTH} / ${POSTER_HEIGHT}` }}
    >
      {width > 0 && (
        <div className="absolute left-0 top-0 origin-top-left" style={{ width: POSTER_WIDTH, height: POSTER_HEIGHT, transform: `scale(${scale})` }}>
          {children}
        </div>
      )}
    </div>
  );
}

const pillClass = (on: boolean) =>
  `h-9 rounded-full px-4 text-sm transition-colors ${on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'}`;

/**
 * html2canvas 在当前页面 body 下临时放一个隐藏小盒子，靠“一行字 + 一张 1px 小图”量文字基线；
 * Tailwind 基础样式把 img 设成 display: block，小图被挤到下一行，量出来的基线偏大，
 * 导出图里所有文字整体往下掉（Windows 微软雅黑上尤其明显）。
 * 导出期间只对这个隐藏小盒子里的 img 改回行内，导出完就移除。
 */
function patchFontMetricsProbe(): () => void {
  const style = document.createElement('style');
  style.textContent = 'body > div[style*="visibility: hidden"] > img { display: inline !important; }';
  document.head.appendChild(style);
  return () => style.remove();
}

const dateStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

export function XhsPosterPanel() {
  const [topics, setTopics] = useState<PosterTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [combo, setCombo] = useState<PosterComboId>('overview');
  const [scope, setScope] = useState<PosterScope>('all');
  const [slots, setSlots] = useState<PosterSlot[]>([]);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isPacking, setIsPacking] = useState(false);
  const [renderForExport, setRenderForExport] = useState(false);
  const [exported, setExported] = useState<ExportedImage[]>([]);
  const exportRefs = useRef<Array<HTMLDivElement | null>>([]);

  const topicsById = useMemo(() => new Map(topics.map((t) => [t.id, t])), [topics]);
  const availableTemplates = useMemo(() => POSTER_TEMPLATES.filter((t) => isTemplateAvailable(t.id, topics)), [topics]);

  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getAdminSpeechTopics({ status: 'active' });
      setTopics(result.items.filter((t) => t.is_active));
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '获取主题失败'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTopics();
  }, [fetchTopics]);

  const regenerate = useCallback(
    (nextCombo: PosterComboId, nextScope: PosterScope) => {
      if (topics.length === 0) {
        setSlots([]);
        return;
      }
      setSlots(buildCombo(nextCombo, { topics, scope: nextScope, random: Math.random }));
    },
    [topics],
  );

  useEffect(() => {
    regenerate(combo, scope);
    // 只在主题加载完成后自动出一组；切换组合、范围在点击时处理
  }, [topics]);

  const changeSlot = (index: number, template: PosterTemplateId) => {
    setSlots((current) => rebuildSlot(current, index, template, { topics, scope, random: Math.random }));
  };

  // 释放上一批导出图片的内存
  useEffect(() => () => exported.forEach((img) => URL.revokeObjectURL(img.url)), [exported]);

  const handleExport = async () => {
    if (slots.length === 0 || isExporting) return;
    setIsExporting(true);
    let unpatch: (() => void) | null = null;
    try {
      const failed = await preloadPosterImages(slots.flatMap((slot) => collectPosterImages(slot, topicsById, topics)));
      setRenderForExport(true);
      // 等离屏成图挂载并完成布局
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const { default: html2canvas } = await import('html2canvas');
      unpatch = patchFontMetricsProbe();
      const stamp = dateStamp();
      const images: ExportedImage[] = [];
      for (let i = 0; i < slots.length; i += 1) {
        const node = exportRefs.current[i];
        if (!node) continue;
        const canvas = await html2canvas(node, {
          useCORS: true,
          scale: 1,
          width: POSTER_WIDTH,
          height: POSTER_HEIGHT,
          backgroundColor: '#faf7f2',
          logging: false,
        });
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('图片生成失败');
        const file = new File([blob], `小红书-${stamp}-${i + 1}.png`, { type: 'image/png' });
        images.push({ file, url: URL.createObjectURL(file) });
      }
      setExported(images);
      if (failed > 0) {
        toast.warning(`有 ${failed} 张素材图读取失败，已用分类色块代替。需要在 COS 开启跨域访问`);
      }
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '生成图片失败，请稍后重试'));
    } finally {
      unpatch?.();
      setRenderForExport(false);
      setIsExporting(false);
    }
  };

  const files = exported.map((img) => img.file);
  const handleZipDownload = async () => {
    if (isPacking || files.length === 0) return;
    setIsPacking(true);
    try {
      const zip = await createPosterZip(files);
      const url = URL.createObjectURL(zip);
      const link = document.createElement('a');
      link.href = url;
      link.download = `小红书-${dateStamp()}-${files.length}张.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // 留时间让浏览器开始读取下载，再释放 ZIP 的内存。
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast.error(extractApiErrorMessage(error, '打包失败，请重试或逐张下载'));
    } finally {
      setIsPacking(false);
    }
  };

  const canShareFiles = typeof navigator !== 'undefined' && files.length > 0 && Boolean(navigator.canShare?.({ files }));

  const handleShare = async () => {
    try {
      await navigator.share({ files });
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') toast.error('系统分享没有打开，请改用“逐张下载”或长按图片保存');
    }
  };

  const handleDownload = async () => {
    for (const img of exported) {
      const link = document.createElement('a');
      link.href = img.url;
      link.download = img.file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // 连续下载间隔一下，避免浏览器拦截
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  };

  const zoomSlot = zoomIndex === null ? null : slots[zoomIndex];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">小红书出图</h2>
        <p className="mt-1 text-sm text-muted-foreground">一键生成 5 张 3:4（1080×1440）图片，可直接发小红书，也可以再拿去可画编辑</p>
      </div>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-10 text-sm font-medium text-muted-foreground">组合</span>
          {POSTER_COMBOS.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={pillClass(combo === item.id)}
              onClick={() => {
                setCombo(item.id);
                regenerate(item.id, scope);
              }}
            >
              {'①②③④⑤'[index]} {item.name}
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">{COMBO_HINTS[combo]}</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-10 text-sm font-medium text-muted-foreground">范围</span>
          {SCOPES.map((item) => (
            <button
              key={item.value}
              type="button"
              className={pillClass(scope === item.value)}
              onClick={() => {
                setScope(item.value);
                regenerate(combo, item.value);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" onClick={() => regenerate(combo, scope)} disabled={isLoading || topics.length === 0}>
            <Shuffle className="mr-1.5 h-4 w-4" />
            换一批
          </Button>
          <Button onClick={() => void handleExport()} disabled={isLoading || slots.length === 0 || isExporting}>
            {isExporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            {isExporting ? '正在生成…' : '生成并保存 5 张'}
          </Button>
        </div>
      </section>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : topics.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
          还没有上架的主题，先去「主题管理」上架主题再来出图
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => void fetchTopics()}>
              <RefreshCw className="mr-1.5 h-4 w-4" />
              重新加载
            </Button>
          </div>
        </div>
      ) : (
        <div className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-5 md:overflow-visible md:px-0">
          {slots.map((slot, index) => (
            <div key={index} className="w-[62%] shrink-0 snap-start space-y-2 sm:w-[40%] md:w-auto md:min-w-0">
              <div className="text-xs font-semibold text-muted-foreground">第 {index + 1} 张</div>
              <div className="flex min-w-0 gap-1.5">
                <div className="min-w-0 flex-1 [&>div]:w-full">
                  <NativeSelect
                    aria-label={`第 ${index + 1} 张的模板`}
                    value={slot.template}
                    onChange={(event) => changeSlot(index, event.target.value as PosterTemplateId)}
                    className="h-8 px-2 pr-7 text-xs"
                  >
                    {availableTemplates.map((t) => (
                      <NativeSelectOption key={t.id} value={t.id}>
                        {t.id} {t.name}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </div>
                <Button variant="outline" size="sm" className="h-8 shrink-0 px-2 text-xs" onClick={() => changeSlot(index, slot.template)}>
                  换主题
                </Button>
              </div>
              <button type="button" className="block w-full cursor-zoom-in" aria-label={`放大查看第 ${index + 1} 张`} onClick={() => setZoomIndex(index)}>
                <ScaledPoster>
                  <Poster slot={slot} topicsById={topicsById} allTopics={topics} />
                </ScaledPoster>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 导出用：原尺寸、不缩放，放在屏幕外 */}
      {renderForExport && (
        <div aria-hidden className="pointer-events-none fixed top-0" style={{ left: -(POSTER_WIDTH + 200) * 6 }}>
          {slots.map((slot, index) => (
            <div
              key={index}
              ref={(node) => {
                exportRefs.current[index] = node;
              }}
              style={{ width: POSTER_WIDTH, height: POSTER_HEIGHT }}
            >
              <Poster slot={slot} topicsById={topicsById} allTopics={topics} />
            </div>
          ))}
        </div>
      )}

      <Dialog open={zoomSlot !== null} onOpenChange={(open) => !open && setZoomIndex(null)}>
        <DialogContent className="max-w-[min(28rem,calc((100vh-9rem)*0.75))] p-4 sm:max-w-[min(28rem,calc((100vh-9rem)*0.75))]">
          <DialogHeader>
            <DialogTitle>第 {(zoomIndex ?? 0) + 1} 张预览</DialogTitle>
            <DialogDescription>实际导出尺寸 1080×1440</DialogDescription>
          </DialogHeader>
          {zoomSlot && (
            <ScaledPoster>
              <Poster slot={zoomSlot} topicsById={topicsById} allTopics={topics} />
            </ScaledPoster>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={exported.length > 0} onOpenChange={(open) => !open && setExported([])}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>5 张图已生成</DialogTitle>
            <DialogDescription>可打包下载全部图片。手机上可用系统分享存到相册；在微信里打开时，请长按图片保存</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {exported.map((img) => (
              <img key={img.url} src={img.url} alt={img.file.name} className="w-full rounded-md border border-border" />
            ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
            {canShareFiles && (
              <Button onClick={() => void handleShare()}>
                <Share2 className="mr-1.5 h-4 w-4" />
                存到相册 / 分享
              </Button>
            )}
            <Button disabled={isPacking} onClick={() => void handleZipDownload()}>
              {isPacking ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
              {isPacking ? '正在打包…' : '打包下载'}
            </Button>
            <Button variant="outline" onClick={() => void handleDownload()}>
              <Download className="mr-1.5 h-4 w-4" />
              逐张下载
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
